import React from 'react';
import DefaultLoading from './DefaultLoading';
import DefaultError from './DefaultError';
import APIError461 from './APIError461';
import APIErrorGeneric from './APIErrorGeneric';

/**
 * APIView Component
 * 
 * Handles all conditional rendering for API responses - loading, errors, and success states.
 * Children only render when data is successfully loaded.
 * 
 * @param {object} props
 * @param {any} props.data - The actual API data
 * @param {object} props.response - Response state object { isLoading, isError, error, refetch }
 * @param {React.ReactNode} [props.loadingFallback] - Custom loading component
 * @param {React.ReactNode} [props.errorFallback] - Custom error component  
 * @param {React.ReactNode} props.children - Content to render when data loads successfully
 */
export function APIView({
  data,
  response,
  loadingFallback,
  errorFallback,
  children
}) {
  //console.log('APIView data:', data);
  const { isLoading, isError, error, refetch } = response;

  // Loading state
  if (isLoading) {
    return loadingFallback || <DefaultLoading />;
  }

  // Error state
  if (isError) {   
    // Handle specific API error codes
    if (error?.errorCode === 461 || error?.code === 461) {
      return <APIError461 data={error.data} />;
    }

    if (error?.errorCode || error?.code) {
      return <APIErrorGeneric data={error.data} />;
    }

    // Handle custom error fallback
    if (errorFallback) {
      return React.cloneElement(errorFallback, { error, refetch });
    }

    // Default error handling
    return <DefaultError error={error} refetch={refetch} />;
  }

  // No data
  if (!data) {
    return null;
  }

  // No children to render
  if (!children) {
    return null;
  }

  // Success state - render children
  return <>{children}</>;
}